<template>
    <div class="flex mt-[42px] mb-[27px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ t("vue.account_settings") }}
        </h1>
    </div>

    <SettingsTabs class="mt-[24px]" tab-key="team-activity-log">
        <div class="mt-[42px] pb-20">
            <DynamicContentTable
                :title="t('vue.team_activity_log_title')"
                :subtitle="t('vue.team_activity_log_subtitle')"
                :rows="logs"
                :columns="columns"
                :endpoint="route('settings.team-activity-log.index')"
                :base-query="baseQuery"
                :table="table"
                :pagination="pagination"
                :search-placeholder="
                    t('vue.team_activity_log_search_placeholder')
                "
                :search-aria-label="
                    t('vue.team_activity_log_search_placeholder')
                "
                :empty-text="t('vue.team_activity_log_empty')"
                :per-page-label="t('vue.per_page')"
                :row-key="(row: ITeamActivityLogItem) => row.id"
            >
                <template #header-extra>
                    <TeamActivityLogFilterBar
                        :range="range"
                        :filters="filters"
                        :filter-options="filterOptions"
                        @change="applyFilters"
                    />
                </template>

                <template #row="{ row }">
                    <td class="whitespace-nowrap text-[13px] text-gray-600">
                        {{ formatOccurredAt(row.occurred_at) }}
                    </td>
                    <td class="min-w-0">
                        <p
                            class="truncate text-[13px] font-medium text-gray-800"
                        >
                            {{
                                row.user_name ??
                                row.user_email ??
                                t("vue.team_activity_log_system")
                            }}
                        </p>
                        <p
                            v-if="row.user_name !== null && row.user_email"
                            class="truncate text-[12px] text-gray-500"
                        >
                            {{ row.user_email }}
                        </p>
                    </td>
                    <td>
                        <span
                            :class="[BADGE_CLASS, 'bg-gray-100 text-gray-700']"
                        >
                            {{ humanizeValue(row.entity_type) }}
                        </span>
                    </td>
                    <td>
                        <span
                            v-if="row.event_code"
                            :class="[BADGE_CLASS, 'bg-blue-50 text-blue-700']"
                        >
                            {{ humanizeValue(row.event_code) }}
                        </span>
                        <span v-else class="text-gray-400">—</span>
                    </td>
                    <td>
                        <span
                            :class="[
                                BADGE_CLASS,
                                'bg-purple-50 text-purple-700',
                            ]"
                        >
                            {{ humanizeValue(row.operation_type) }}
                        </span>
                    </td>
                    <td class="text-[13px] text-gray-500">
                        {{ row.source ?? "—" }}
                    </td>
                    <td
                        class="max-w-0 truncate text-[13px] text-gray-600"
                        :title="row.message ?? undefined"
                    >
                        {{ row.message ?? "—" }}
                    </td>
                </template>
            </DynamicContentTable>
        </div>
    </SettingsTabs>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { format } from "date-fns";
import { useTranslations } from "@/composables/useTranslations";
import DynamicContentTable from "@/components/Content/DynamicContentTable.vue";
import SettingsTabs from "@/Pages/Settings/components/SettingsTabs.vue";
import TeamActivityLogFilterBar from "./components/TeamActivityLogFilterBar.vue";
import { humanizeValue } from "./humanizeValue";
import type {
    IActivityLogFilters,
    IActivityLogRange,
} from "@/types/ActivityLog/IActivityLogFilters";
import type {
    ITeamActivityLogItem,
    ITeamActivityLogPageProps,
} from "@/types/ActivityLog/ITeamActivityLog";

const props = defineProps<ITeamActivityLogPageProps>();

const { t } = useTranslations();

const BADGE_CLASS =
    "inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-medium capitalize";

const columns = computed(() => [
    {
        key: "occurred_at",
        label: t("vue.team_activity_log_col_time"),
        sortable: true,
        sortKey: "created_at",
        colClass: "w-[150px]",
    },
    {
        key: "user",
        label: t("vue.team_activity_log_col_user"),
        sortable: true,
        sortKey: "user",
        colClass: "w-[210px]",
    },
    {
        key: "entity_type",
        label: t("vue.team_activity_log_col_entity"),
        sortable: true,
        colClass: "w-[130px]",
    },
    {
        key: "event_code",
        label: t("vue.team_activity_log_col_event"),
        sortable: true,
        colClass: "w-[200px]",
    },
    {
        key: "operation_type",
        label: t("vue.team_activity_log_col_operation"),
        sortable: true,
        colClass: "w-[120px]",
    },
    {
        key: "source",
        label: t("vue.team_activity_log_col_source"),
        sortable: true,
        colClass: "w-[100px]",
    },
    {
        key: "message",
        label: t("vue.team_activity_log_col_message"),
        sortable: true,
    },
]);

const buildFilterQuery = (
    range: IActivityLogRange,
    filters: IActivityLogFilters,
): Record<string, string> => {
    const query: Record<string, string> = { from: range.from, to: range.to };

    for (const [key, value] of Object.entries(filters)) {
        if (value !== null) {
            query[key] = value;
        }
    }

    return query;
};

const baseQuery = computed(
    (): Record<string, string> => buildFilterQuery(props.range, props.filters),
);

const formatOccurredAt = (occurredAt: string | null): string =>
    occurredAt === null
        ? "—"
        : format(new Date(occurredAt), "d. M. yyyy HH:mm");

const applyFilters = (
    range: IActivityLogRange,
    filters: IActivityLogFilters,
): void => {
    router.get(
        route("settings.team-activity-log.index"),
        {
            ...buildFilterQuery(range, filters),
            q: props.table.q ?? undefined,
            sort_by: props.table.sort_by,
            sort_dir: props.table.sort_dir,
            per_page: props.table.per_page,
            current_page: 1,
        },
        {
            replace: true,
            preserveScroll: true,
            preserveState: true,
        },
    );
};
</script>
